import React, { useCallback, useEffect, useRef, useState } from 'react';
import { render } from 'react-dom';
import { Provider as ReduxProvider, useDispatch, useSelector } from 'react-redux';

import configureStore from 'store';

import { getCurrency, getDictionary, getLanguage, getMarket } from '@travel/i18n';
import { List } from '@travel/icons/ui';
import {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
  useCurrencyChangeCallback,
  useDeviceType,
} from '@travel/traveler-core/hooks';
import { toSEOSearchString } from '@travel/traveler-core/utils';
import { FlatButton, Tag } from '@travel/ui';
import { cx } from '@travel/utils';

import ProviderSummary from 'components/ProviderSummary';
import SortSelectBox from 'components/SortSelectBox';
import WithSkeletonLoading from 'components/WithSkeletonLoading';
import { STAR_RATING_KEY } from 'pages/ProviderListPage/constants';

import { pushLocation, replaceLocation } from 'store/__router/actions';
import { getLocation } from 'store/__router/selectors';
import { getIsGlobalCurrency } from 'store/markets/selectors';
import {
  getFiltersContractType,
  getIsFetchingList,
  getPlaceName,
} from 'store/providerList/selectors';

import { PROVIDER_CONTRACT_TYPES } from 'constants/provider';
import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import type { ProviderItem, SearchFormQuery } from 'ProviderList-Types';

import Map from '../Map';
import { createPopupClass } from '../Map/MapCard';
import ProviderList from '../ProviderList';
import ProviderSummarySlider from '../ProviderSummarySlider';
import useInitialScrollPosition from './useInitialScrollPosition';

import styles from './listAndMap.module.scss';

type Props = {
  /** Object of current page params */
  params: { pageIdentifier: string };
  /** Object of current page query */
  query: SearchFormQuery;
  /** Flag to define whether the map is expanded or not */
  isExpandedMap?: boolean;
  /** Callback to be called when remove button of coupon name is clicked */
  onRemoveCoupon?: () => void;
};

type MapPinStoreType = {
  [id: string]: any; // TODO: Put Markerwithlabel type
};

function ListAndMap(props: Props) {
  const { params, query, isExpandedMap } = props;
  const dispatch = useDispatch();
  const deviceType = useDeviceType();

  const isFetchingList = useSelector(getIsFetchingList);
  const location = useSelector(getLocation);
  const currency = useSelector(getCurrency);
  const language = useSelector(getLanguage);
  const market = useSelector(getMarket);
  const isGlobalCurrency = useSelector(getIsGlobalCurrency);
  const areaName = useSelector(getPlaceName);
  const { onDefaultScroll } = useInitialScrollPosition(isFetchingList);
  const contractType = useSelector(getFiltersContractType);
  const isDirectContract = contractType === PROVIDER_CONTRACT_TYPES.DIRECT;

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const prevMarkerRef = useRef<google.maps.Marker | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<MapPinStoreType>({});

  const isSP = deviceType === MOBILE_SCREEN;
  const isPC = deviceType === DESKTOP_SCREEN;
  const setMapInstance = useCallback(mapObj => (mapRef.current = mapObj), []);

  /* Id of previously clicked item id */
  const prevItemIdRef = useRef(selectedItemId);
  const setSelectedItemWithRef = useCallback((id: string | null) => {
    prevItemIdRef.current = id;
    setSelectedItemId(id);
  }, []);

  /* Reset all map pins */
  const resetMarkers = useCallback(() => {
    Object.values(markersRef.current).map(marker => marker.setMap(null));
    markersRef.current = {};
  }, []);

  /* Reset selected map pin by id */
  const resetMapPin = useCallback((id: string) => {
    if (markersRef.current?.[id]) {
      markersRef.current[id].setMap?.(null);
      delete markersRef.current[id];
    }
  }, []);

  //TODO: will hoist this function to page level (TIDP-47274)
  /* Currency change handler */
  const onCurrencyChange = useCallback(() => {
    resetMarkers();
    // To deal with array, we process starRating separately
    const { [STAR_RATING_KEY]: starRatingValues, ...restQuery } = query;
    const searchParams = new URLSearchParams(
      toSEOSearchString({ ...restQuery, maxPrice: '', minPrice: '' }),
    );
    starRatingValues !== undefined &&
      (Array.isArray(starRatingValues)
        ? starRatingValues?.map(value => searchParams.append(STAR_RATING_KEY, value))
        : searchParams.append(STAR_RATING_KEY, starRatingValues));

    // When we change currency, we initialize min-max value
    dispatch(
      replaceLocation({
        pathname: paths.providerList.pathResolver(params.pageIdentifier),
        search: searchParams.toString(),
      }),
    );
  }, [dispatch, resetMarkers, query, params.pageIdentifier]);
  useCurrencyChangeCallback(onCurrencyChange);

  const storeMapPinInListAndMap = useCallback((id: string, mapPinObj: google.maps.Marker) => {
    markersRef.current = { ...markersRef.current, [id]: mapPinObj };
  }, []);

  useEffect(() => {
    if (selectedItemId) {
      markersRef.current[selectedItemId]?.set('labelClass', styles.labelClicked);
    }
    // add style for selected item when re-render
     
  }, [selectedItemId, isExpandedMap]);

  const initializePinAndCard = useCallback(() => {
    if (!isSP) {
      prevMarkerRef.current?.setMap(null);
      prevMarkerRef.current = null;
    }

    // If prevMapPin exists, change its style as unselected
    if (prevItemIdRef.current) {
      const prevPin = markersRef.current[prevItemIdRef.current || ''];
      if (prevPin) {
        prevPin.set('labelClass', styles.label);
      }
      setSelectedItemWithRef(null);
    }
  }, [isSP, setSelectedItemWithRef]);

  const onProviderSummaryClick = useCallback(
    (item: ProviderItem) => {
      dispatch(
        pushLocation({
          pathname: paths.providerInfo.pathResolver(item.pathId),
          search: location.search,
        }),
      );
    },
    [dispatch, location.search],
  );

  const dictionary = useSelector(getDictionary);

  const createProviderSummary = useCallback(
    (item: ProviderItem) => {
      const medium = item.media?.find(medium => !!medium.url);
      const store = configureStore({ _i18n: { dictionary } });
      const isDirectContract = item.contractType === PROVIDER_CONTRACT_TYPES.DIRECT;

      return (
        <ReduxProvider store={store}>
          <ProviderSummary
            className={styles.providerSummary}
            id={item.id}
            price={item.price}
            name={item.name}
            reviewScore={item.reviews.score}
            totalReviews={item.reviews.count}
            reviewLabel={item.reviews.label}
            media={medium}
            starRating={item.starRating}
            onClick={() => onProviderSummaryClick(item)}
            isExpandedMap={isExpandedMap}
            language={language}
            currency={currency}
            market={market.marketCode}
            isGlobalCurrency={isGlobalCurrency}
            isDirectContract={isDirectContract}
          />
        </ReduxProvider>
      );
    },
    [
      currency,
      isGlobalCurrency,
      language,
      market.marketCode,
      onProviderSummaryClick,
      isExpandedMap,
      dictionary,
    ],
  );

  // Below function is mixed up with creating mapCard and manage clicked mapCard state
  const createMapCard = useCallback(
    (map, mapPin, item) => {
      // Google API can take only DOM Node, not ReactNode
      const cardWrapper = document.createElement('div');
      if (typeof item === 'string') {
        render(
          <Tag
            className={cx(styles.mapLabel, styles.labelAnchor)}
            type={'filled'}
            color={'white'}
            data-testid="listAndMap-searchedMapPin-tag"
          >
            {areaName}
          </Tag>,
          cardWrapper,
        );
      } else {
        render(createProviderSummary(item), cardWrapper);
      }

      const Popup = createPopupClass();
      const position = mapPin.getPosition();
      // @ts-ignore
      const mapCard = new Popup(position, cardWrapper);

      if (prevMarkerRef.current) {
        prevMarkerRef.current?.setMap(null); // Close previously open mapCard
      }

      prevMarkerRef.current = mapCard; // Remember created mapCard
      mapCard.setMap?.(map); // Display mapCard on the map by setting map property of mapCard
    },
    [areaName, createProviderSummary],
  );

  const resetPrevPinStyle = () => {
    const prevPin = markersRef.current[prevItemIdRef.current || ''];
    if (prevItemIdRef.current && prevPin) {
      prevPin.set('labelClass', styles.label);
    }
  };

  const onChangeMapPinStyle = useCallback(
    (ItemId: string) => {
      if (prevItemIdRef.current !== ItemId) {
        resetPrevPinStyle();
        setSelectedItemWithRef(ItemId);
      }
    },
    [setSelectedItemWithRef],
  );

  const onMouseOver = useCallback((ItemId: string) => onChangeMapPinStyle(ItemId), [
    onChangeMapPinStyle,
  ]);

  const onMapPinClick = useCallback(
    (item: ProviderItem) => {
      onChangeMapPinStyle(item.id);

      if (!isSP && !!mapRef.current && markersRef.current[item.id]) {
        createMapCard(mapRef.current, markersRef.current[item.id], item);
      }

      // remove search spot tag
      if (isSP) {
        prevMarkerRef.current?.setMap(null); // Close previously open mapCard
        window.scrollTo(0, 0); // hidden the list when click tag
      }
    },
    [createMapCard, isSP, onChangeMapPinStyle],
  );

  const onSearchedSpotMapPinClick = useCallback(
    (itemId: string, shouldNotResetScroll?: boolean) => {
      createMapCard(mapRef.current, markersRef.current[itemId], itemId);
      // reset the style for prevPin
      resetPrevPinStyle();
      // hidden the list when click tag
      if (isSP && !shouldNotResetScroll) {
        window.scrollTo(0, 0);
      }
    },
    [createMapCard, isSP],
  );

  const onGoBackClick = useCallback(() => {
    onDefaultScroll();
    initializePinAndCard();
  }, [initializePinAndCard, onDefaultScroll]);

  // mobile only
  const onSlideChange = useCallback(
    provider => {
      onChangeMapPinStyle(provider.id);
      mapRef.current?.panTo(provider?.location);
    },
    [onChangeMapPinStyle],
  );

  return (
    <div
      className={cx(styles.wrapper, !isExpandedMap && styles.expandedListView)}
      data-testid={`listAndMap-wrapper-${isExpandedMap ? 'map' : 'list'}`}
    >
      {/*  list */}
      <div className={styles.providerListWrapper}>
        <WithSkeletonLoading
          isLoading={isFetchingList}
          count={3}
          row={3}
          length="long"
          customStyle="textWithImageRow"
          className={styles.loaderWrapper}
          skeletonItemClassName={styles.loader}
          skeletonImageClassName={styles.loaderImage}
        >
          {!isSP && <SortSelectBox query={query} params={params} />}
          <ProviderList
            query={query}
            params={params}
            onMouseOver={isPC ? onMouseOver : undefined}
            isExpandedMap={isExpandedMap}
            onRemoveCoupon={props.onRemoveCoupon}
          />
        </WithSkeletonLoading>
      </div>

      {/* map */}
      <div className={styles.mapWrapper}>
        <Map
          params={props.params}
          query={props.query}
          initializePinAndCard={initializePinAndCard}
          storeMapPinInListAndMap={storeMapPinInListAndMap}
          onMapPinClick={onMapPinClick}
          onSearchedSpotMapPinClick={onSearchedSpotMapPinClick}
          setMapInstance={setMapInstance}
          language={language}
          currency={currency}
          isGlobalCurrency={isGlobalCurrency}
          onResetMapPins={resetMarkers}
          resetMapPin={resetMapPin}
        />

        {isSP && (
          <div className={styles.goBackAndProviderSummary}>
            <FlatButton
              className={cx(
                selectedItemId ? styles.pinSelectedGoBack : styles.goBack,
                styles.buttonStyle,
              )}
              icon={<List size={16} />}
              classType="list"
              onClick={onGoBackClick}
            >
              <Translate id="Provider_List.Map_View.Back" />
            </FlatButton>
            {selectedItemId && (
              <ProviderSummarySlider
                onSlideChange={onSlideChange}
                selectedProviderId={selectedItemId}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(ListAndMap);
